import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Text from "@/components/ui/text";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { Close } from "@dreamhub/designsystem/icons";
import { DrawerTitle as CustomDrawerTitle } from "../../FieldManagement/components/DrawerTitle";
import { getFieldDisplayTypeIcon } from "../../FieldManagement/hooks";
import { FieldDisplayType } from "../../FieldManagement/types";
import { generateFieldDisplayTypeOptions } from "../../FieldManagement/utils";
import AddActionButton from "@/components/ui/add-action-button";
import { WorkflowRequiredFieldSection } from "./components/WorkflowRequiredFieldSection";
import { AIAutofillSection } from "./components/AIAutofillSection";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { AUTOFILL_TRANSLATION_KEY } from "./const";
import {
  DreamerFlow,
  useDeleteAutofillDraftQuestion
} from "./hooks/useAutofillApi";

const TRANSLATION_PREFIX = "settings.deals.workflow.addSimpleField";

export const defaultFieldDisplayTypeByWorkflowType: Partial<
  Record<QuestionnaireType, FieldDisplayType>
> = {
  [QuestionnaireType.SIMPLE]: FieldDisplayType.BOOL,
  [QuestionnaireType.MAPPING]: FieldDisplayType.PEOPLE_IN_COMPANY,
  [QuestionnaireType.SCORE]: FieldDisplayType.SWITCH
};

interface AddSimpleWorkflowFieldDrawerProps {
  workflowType: QuestionnaireType;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  questionnaireId: string;
  enableAutofill?: boolean;
  onSave?: (fieldData: any) => void;
}

export const AddSimpleWorkflowFieldDrawer: React.FC<
  AddSimpleWorkflowFieldDrawerProps
> = ({
  workflowType,
  isOpen,
  onOpenChange,
  questionnaireId,
  enableAutofill = true,
  onSave
}) => {
  const { t } = useTranslation();
  const showAutofill = enableAutofill;
  const formRef = useRef<HTMLFormElement>(null);
  const savedRef = useRef(false);
  const deleteDraft = useDeleteAutofillDraftQuestion();

  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldHelpText, setFieldHelpText] = useState("");
  const [showHelpText, setShowHelpText] = useState(false);
  const [isRequiredForPass, setIsRequiredForPass] = useState(false);
  const [filledByDreamer, setFilledByDreamer] = useState(false);
  const [autofillInstructions, setAutofillInstructions] = useState<
    string | null
  >(null);
  const [autofillQuestionId, setAutofillQuestionId] = useState<string | null>(
    null
  );
  const [dreamerFlow, setDreamerFlow] = useState<DreamerFlow | null>(null);
  const [autofillSaveBlocked, setAutofillSaveBlocked] = useState(false);
  const [autofillBlockTooltip, setAutofillBlockTooltip] = useState<
    string | null
  >(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultFieldDisplayType =
    defaultFieldDisplayTypeByWorkflowType[workflowType] ||
    FieldDisplayType.BOOL;

  useEffect(() => {
    if (isOpen) {
      setFieldLabel("");
      setFieldHelpText("");
      setShowHelpText(false);
      setIsRequiredForPass(false);
      setFilledByDreamer(false);
      setAutofillInstructions(null);
      setAutofillQuestionId(null);
      setDreamerFlow(null);
      setAutofillSaveBlocked(false);
      setAutofillBlockTooltip(null);
      setIsFormValid(false);
      savedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && formRef.current) {
      const fieldTypeInput = formRef.current.querySelector(
        'input[name="fieldType"]'
      ) as HTMLInputElement;
      if (fieldTypeInput) {
        fieldTypeInput.value = defaultFieldDisplayType;
        const event = new Event("change", { bubbles: true });
        fieldTypeInput.dispatchEvent(event);
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    if (autofillQuestionId && !savedRef.current) {
      deleteDraft.mutate({ pathParams: { questionId: autofillQuestionId } });
    }
    setFieldLabel("");
    setFieldHelpText("");
    setShowHelpText(false);
    setIsRequiredForPass(false);
    setFilledByDreamer(false);
    setIsFormValid(false);
    onOpenChange(false);
  };

  const handleFormValid = () => {
    setIsFormValid(fieldLabel.trim().length > 0);
  };

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting || autofillSaveBlocked) return;

    setIsSubmitting(true);
    try {
      const fieldData = {
        fieldLabel: fieldLabel.trim(),
        fieldHelpText: fieldHelpText.trim() || null,
        fieldType:
          workflowType === QuestionnaireType.MAPPING
            ? "peopleInCompany"
            : "bool",
        defaultValue:
          workflowType === QuestionnaireType.MAPPING ? null : "none",
        isRequiredForPass,
        filledByDreamer,
        autofillInstructions: filledByDreamer ? autofillInstructions : null,
        autofillQuestionId: filledByDreamer ? autofillQuestionId : null,
        dreamerFlow: filledByDreamer ? dreamerFlow : null,
        showInCreation: false,
        isRequired: false
      };

      savedRef.current = true;
      onSave?.(fieldData);
      handleClose();
    } catch (error) {
      console.error("Failed to add workflow field:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldLabelFields: FormFieldNewType[] = [
    {
      name: "fieldLabel",
      type: "text" as const,
      label: t(`${TRANSLATION_PREFIX}.fields.fieldName`),
      required: true,
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_PREFIX}.placeholders.addFieldLabel`),
        value: fieldLabel,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setFieldLabel(e.target.value);
          setIsFormValid(e.target.value.trim().length > 0);
        }
      }
    }
  ];

  if (showHelpText) {
    fieldLabelFields.push({
      name: "fieldHelpText",
      type: "text" as const,
      label: t(`${TRANSLATION_PREFIX}.fields.fieldHelpText`),
      required: false,
      fieldActionProps: {
        placeholder: "",
        value: fieldHelpText,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          setFieldHelpText(e.target.value);
        }
      }
    });
  }

  // // Create fieldData for FieldPopulation component
  // const fieldData: APIFieldItem = {
  //   id: "temp-workflow-field",
  //   fieldKey: "temp-workflow-field",
  //   label: fieldLabel || "New Workflow Field",
  //   displayType: defaultFieldDisplayType,
  //   fieldType: SettingsFieldType.CUSTOM,
  //   dataSource: FieldDataSource.MANUAL,
  //   order: 0,
  //   filledByDreamer,
  //   dreamerFlow: null,
  //   showInCreation: false,
  //   showInDetailedView: true,
  //   required: false,
  //   config: null,
  //   helpText: fieldHelpText || null,
  // };

  const formSections: FormSectionProps[] = [
    {
      id: "field-label",
      divider: true,

      dividerClassName: "my-2",
      fields: fieldLabelFields,
      bottomCustomComponent: !showHelpText ? (
        <div className="flex justify-start mt-[-22px]">
          <AddActionButton
            label={t(`${TRANSLATION_PREFIX}.actions.addHelpText`)}
            onClick={() => setShowHelpText(true)}
            textVariant="body"
          />
        </div>
      ) : undefined
    },
    // {
    //   id: "population",
    //   divider: true,
    //   bottomCustomComponent: React.createElement(FieldPopulation, {
    //     fieldData,
    //     onFilledByDreamerChange: setFilledByDreamer,
    //   }),
    // },
    {
      id: "field-type",
      divider: true,

      dividerClassName: "my-2",
      fields: [
        {
          name: "fieldType",
          type: "singleSelect" as const,
          label: t(`${TRANSLATION_PREFIX}.fields.fieldType`),
          required: true,
          defaultValue: defaultFieldDisplayType,
          hintText:
            workflowType === QuestionnaireType.MAPPING
              ? t(`${TRANSLATION_PREFIX}.helpTexts.mappingFieldTypeRestriction`)
              : workflowType === QuestionnaireType.SIMPLE ||
                  workflowType === QuestionnaireType.SCORE
                ? t(`${TRANSLATION_PREFIX}.helpTexts.fieldTypeRestriction`)
                : "",
          fieldActionProps: {
            fieldName: "fieldType",
            options: generateFieldDisplayTypeOptions(t, true, true),
            placeholder: t(
              `${TRANSLATION_PREFIX}.placeholders.selectFieldType`
            ),
            renderIcon: (value: string | number) =>
              getFieldDisplayTypeIcon(value as FieldDisplayType, "secondary"),
            disabled: true,
            value: defaultFieldDisplayType
          }
        }
      ] as FormFieldNewType[]
    },
    ...(workflowType !== QuestionnaireType.MAPPING
      ? [
          {
            id: "default-entry",
            divider: true,

            dividerClassName: "my-2",
            fields: [
              {
                name: "defaultEntry",
                type: "singleSelect" as const,
                label: t(`${TRANSLATION_PREFIX}.fields.defaultEntry`),
                required: false,
                defaultValue: "none",
                fieldActionProps: {
                  fieldName: "defaultEntry",
                  options: [
                    {
                      id: "none",
                      displayName: t(`${TRANSLATION_PREFIX}.placeholders.none`)
                    }
                  ],
                  placeholder: t(`${TRANSLATION_PREFIX}.placeholders.none`),
                  disabled: true,
                  value: "none"
                }
              }
            ] as FormFieldNewType[]
          }
        ]
      : [])
  ];

  return (
    <Drawer
      open={isOpen}
      onOpenChange={onOpenChange}
      direction="right"
      modal={false}
      dismissible={false}
    >
      <AppDrawerContent isSettingsDrawer>
        <DrawerHeader>
          <DrawerTitle>
            <CustomDrawerTitle title={t(`${TRANSLATION_PREFIX}.title`)} />
          </DrawerTitle>
          <IconButton
            data-test="close-add-workflow-field-drawer"
            onClick={handleClose}
            variant="iconNoBg"
            size="md"
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <div className="p-5 w-full overflow-y-auto max-h-[calc(100vh-200px)]">
          <FormNew
            sections={formSections}
            isFormValid={handleFormValid}
            onSubmit={handleSubmit}
            formRef={formRef}
            onSetValue={() => {}}
          />

          {showAutofill ? (
            <>
              <div className="w-full border-t-[0.5px] border-border-border1 my-6" />
              <AIAutofillSection
                fieldName={fieldLabel}
                fieldType={defaultFieldDisplayType}
                helpText={fieldHelpText}
                questionnaireId={questionnaireId}
                enabled={filledByDreamer}
                onEnabledChange={setFilledByDreamer}
                onInstructionsChange={setAutofillInstructions}
                onQuestionIdChange={setAutofillQuestionId}
                onDreamerFlowChange={setDreamerFlow}
                onSaveBlockedChange={(blocked, tooltip) => {
                  setAutofillSaveBlocked(blocked);
                  setAutofillBlockTooltip(tooltip ?? null);
                }}
                translationPrefix={AUTOFILL_TRANSLATION_KEY}
                className=""
              />
              <div className="w-full border-t-[0.5px] border-border-border1 my-6" />
            </>
          ) : null}

          <WorkflowRequiredFieldSection
            isRequiredForPass={isRequiredForPass}
            onRequiredForPassChange={setIsRequiredForPass}
            translationPrefix={
              workflowType === QuestionnaireType.MAPPING
                ? "settings.deals.workflow.requiredFieldSection"
                : `${TRANSLATION_PREFIX}.requiredForPass`
            }
            className={showAutofill ? "" : "mt-6"}
          />

        </div>

        <DrawerFooter>
          <div className="flex justify-between gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
            <div />
            <div className="flex gap-2 ml-auto">
              <Button
                variant="secondaryNoBg"
                onClick={handleClose}
                className="h-9"
              >
                {t(`${TRANSLATION_PREFIX}.actions.cancel`)}
              </Button>
              {autofillBlockTooltip ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        tabIndex={0}
                        aria-disabled="true"
                        className="inline-flex cursor-not-allowed"
                      >
                        <Button disabled className="h-9">
                          {t(`${TRANSLATION_PREFIX}.actions.addField`)}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <Text variant="smBody" className="max-w-xs">
                        {autofillBlockTooltip}
                      </Text>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting || autofillSaveBlocked}
                  className="h-9"
                >
                  {t(`${TRANSLATION_PREFIX}.actions.addField`)}
                </Button>
              )}
            </div>
          </div>
        </DrawerFooter>
      </AppDrawerContent>
    </Drawer>
  );
};
